/*
 * Public (unauthenticated) read helpers for the blogs API.
 * Admin writes live in src/api/blogAdmin.js.
 *
 * ---------------------------------------------------------------------------
 * BACKEND CONTRACT
 * ---------------------------------------------------------------------------
 *
 *   GET /api/blogs?limit=12&category=World&q=budget&lastEvaluatedKey=<json>
 *     → { content: BlogSummary[], lastEvaluatedKey: object|null,
 *         hasMore: boolean, size: number }
 *
 *     CURSOR-paged, not page-numbered. There is no totalElements/totalPages and
 *     there cannot be — the store cannot count matches without reading every
 *     post. So: no numbered pager, no "showing X of Y", no jump-to-last-page.
 *
 *   GET /api/blogs/{blogId}
 *     → BlogDetail (404 when missing — NOT 200 with an empty body, or the
 *       frontend cannot tell "no such post" from "post with no content")
 *
 *   GET /api/blogs/{blogId}/related?limit=3
 *     → BlogSummary[]  (optional — the frontend degrades to an empty list, it
 *       just loses some internal linking)
 *
 *   GET /api/blogs/featured
 *     → BlogSummary[] with AT MOST ONE item. [] means nothing is pinned. Never a
 *       404, never more than one post — the backend clears the previous pin on
 *       write, so the frontend never has to pick between two. No `content` field:
 *       this is a card/hero shape, link through to the detail route for the body.
 *       The frontend reads it as a LIST (see fetchFeaturedBlogs) and does not
 *       depend on the one-item limit, so lifting it needs no frontend change.
 *
 * BlogSummary: { blogId, title, category, tags[], author, excerpt, image,
 *                createdOn, updatedOn, featured }
 * BlogDetail:  BlogSummary + { content (HTML), urlLink? }
 *
 * `category` must be an exact display name ("Entertainment") or be omitted;
 * anything else is a 400. Route slugs are mapped back to display names by
 * categoryFromSlug() before they reach here.
 *
 * `q` matches the TITLE ONLY, case-insensitive substring. It does not search
 * excerpt, body or tags.
 */

import { API_ENDPOINTS } from '../api';

// Six fills two rows of three on desktop and keeps the first paint small on a phone.
export const DEFAULT_LIMIT = 6;

/*
 * Which single-post URL shape this backend answers on, learned on first use.
 * 'unknown' → try the documented flat path, fall back to the composite one.
 * See fetchBlogById.
 */
let detailPathStyle = 'unknown';

/**
 * Normalise the id field across possible backend spellings, and guarantee a
 * category so URL building never produces "/blog/undefined/...".
 */
export function normaliseBlog(b) {
  if (!b) return null;
  const blogId = b.blogId || b.id || b._id;
  if (!blogId) return null;
  return { ...b, blogId: String(blogId), category: b.category || 'Others', tags: b.tags || [] };
}

/**
 * Normalise a cursor-paged list response.
 *
 * Tolerant of three envelope spellings, because the sibling project
 * (offerBazaarFrontend) talks to a backend of the same lineage that uses
 * `{ blogs: [...] }`, the documented BlogFanda contract uses
 * `{ content: [...] }`, and either may return a bare array before pagination is
 * wired up. Accepting all three costs nothing and avoids an empty page if the
 * backend ships the other spelling.
 *
 * `hasMore` drives paging, and a short page is NOT the end of the list — `q` is
 * applied server-side after the window is read, so a page with fewer items than
 * `limit` while more data exists is normal. Treating `size < limit` as the end
 * would silently truncate search results.
 *
 * When the backend omits `hasMore` entirely, fall back to the presence of a
 * cursor — which is exactly how offerBazaarFrontend decides. Without this
 * fallback, a backend that only sends `lastEvaluatedKey` would look like a
 * single-page result and everything past page one would be unreachable.
 */
function normaliseList(data) {
  if (Array.isArray(data)) {
    return { blogs: data.map(normaliseBlog).filter(Boolean), cursor: null, hasMore: false };
  }

  const list = data.content || data.blogs || [];
  const cursor = data.lastEvaluatedKey || null;

  return {
    blogs: list.map(normaliseBlog).filter(Boolean),
    cursor,
    hasMore: data.hasMore === undefined ? Boolean(cursor) : Boolean(data.hasMore),
  };
}

async function getJson(url) {
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (res.status === 404) return null;
  if (!res.ok) {
    // The backend sends { code, message, field, traceId }. Show `message` when
    // it is there — "Request failed (400)" tells the reader nothing.
    let detail = '';
    let traceId = '';
    try {
      const body = await res.json();
      detail = body.message || body.error || '';
      traceId = body.traceId || '';
    } catch (_) {
      /* not JSON */
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    // Kept on the error so a 500 can surface it for support.
    if (traceId) err.traceId = traceId;
    throw err;
  }
  return res.json();
}

/**
 * Fetch one page of posts.
 *
 * @param {object}  opts
 * @param {string} [opts.category] exact display name, or 'All'/undefined for everything
 * @param {number} [opts.limit=12]
 * @param {string} [opts.query]    free-text, matches the title only
 * @param {object} [opts.cursor]   the previous response's lastEvaluatedKey, verbatim
 * @returns {Promise<{blogs: object[], cursor: object|null, hasMore: boolean}>}
 */
export async function fetchBlogs({ category, limit = DEFAULT_LIMIT, query, cursor } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (category && category !== 'All') params.set('category', category);
  if (query) params.set('q', query);

  /*
   * The cursor is OPAQUE: round-trip it verbatim, never read or rebuild its
   * fields. URLSearchParams.toString() percent-encodes values itself, so the
   * JSON is passed raw here — wrapping it in encodeURIComponent as well would
   * double-encode it and the backend would fail to parse it.
   */
  if (cursor) params.set('lastEvaluatedKey', JSON.stringify(cursor));

  const data = await getJson(`${API_ENDPOINTS.BLOGS}?${params}`);
  if (!data) throw new Error('Failed to load articles');
  return normaliseList(data);
}

/**
 * Fetch a single post. Resolves to null when it does not exist.
 *
 * The documented contract is `/api/blogs/{blogId}`. offerBazaarFrontend, talking to
 * a backend of the same lineage, uses `/api/blogs/{category}/{id}` — the category
 * is part of that store's composite key. If the flat path 404s and we know the
 * category (it is in the article URL), retry the composite one before giving up,
 * so this works against either without a config change.
 *
 * @param {string} id
 * @param {string} [category] display name, used only for the fallback path
 */
export async function fetchBlogById(id, category) {
  if (!id) return null;

  const flat = () => getJson(`${API_ENDPOINTS.BLOGS}/${encodeURIComponent(id)}`);
  const composite = () =>
    getJson(`${API_ENDPOINTS.BLOGS}/${encodeURIComponent(category)}/${encodeURIComponent(id)}`);

  let data;

  // Once one shape has answered, stop probing the other: otherwise every article
  // view against a composite-key backend wastes a request on a known 404.
  if (detailPathStyle === 'composite' && category) {
    data = await composite();
  } else {
    data = await flat();
    if (data) {
      detailPathStyle = 'flat';
    } else if (category) {
      data = await composite();
      if (data) detailPathStyle = 'composite';
    }
  }

  // Some responses wrap the post as { blog: {...} }.
  return normaliseBlog(data && data.blog ? data.blog : data);
}

/**
 * Posts to suggest at the end of an article. Never throws — related posts are a
 * nice-to-have, and a failure here must not blank out the article itself.
 */
export async function fetchRelatedBlogs(blog, limit = 3) {
  if (!blog) return [];
  try {
    const url = `${API_ENDPOINTS.BLOGS}/${encodeURIComponent(blog.blogId)}/related?limit=${limit}`;
    const data = await getJson(url);
    return (Array.isArray(data) ? data : []).map(normaliseBlog).filter(Boolean);
  } catch (_) {
    return [];
  }
}

/**
 * The posts the backend has pinned, newest first. [] when nothing is pinned.
 *
 * A single indexed lookup — do NOT go back to reading a page of /api/blogs and
 * filtering on `featured` client-side, which cost a full list read and could only
 * ever see posts inside the window it happened to fetch.
 *
 * Returns the whole ARRAY rather than unwrapping one post. Today's backend keeps
 * at most one pin (it clears the previous one on write), so in practice this is
 * a 0- or 1-item list and the home page tops its rail up from the latest posts.
 * Returning the list means that if pinning ever becomes multi-post, the rail
 * fills with real pins without a frontend change.
 *
 * Never throws, for the same reason fetchRelatedBlogs does not: the lead story is
 * a nice-to-have, and neither "nothing is featured" nor a failed request is an
 * error the reader should be shown — both just mean fall back to the newest.
 */
export async function fetchFeaturedBlogs() {
  try {
    const data = await getJson(`${API_ENDPOINTS.BLOGS}/featured`);
    const list = Array.isArray(data) ? data : [];
    return list.map(normaliseBlog).filter(Boolean);
  } catch (_) {
    return [];
  }
}
